import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Briefcase, Sparkles, TrendingUp, Users, Building2, DollarSign, GraduationCap, MapPin, Clock, ChevronRight, BarChart3, Download, Mail, HelpCircle, BookOpen, Map } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post("/auth/login", loginData);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("company", JSON.stringify(response.data.company));
      toast.success("Welcome back!");
      
      if (response.data.user.has_seen_welcome === false) {
        navigate("/welcome");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Section - Hero + Login */}
      <div className="flex flex-1">
        {/* Left Side - Hero Section */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 text-white p-12 flex-col justify-between relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3">
              <Briefcase className="w-10 h-10" />
              <div>
                <h1 className="text-3xl font-bold">SmartReqs</h1>
                <p className="text-sm text-slate-300">CaioAbruzzo LLC</p>
              </div>
            </div>
            
            <div className="max-w-lg -mt-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_req-builder-5/artifacts/1k8n0per_vecteezy_hrm-or-human-resource-management-focus-to-manager-icon_25025436.jpg"
                alt="HR Management"
                className="w-96 h-96 object-contain -mb-8"
              />
              
              <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                AI-Powered Job Requisitions in Minutes
              </h2>
              <p className="text-xl text-blue-100 mb-16">
                Generate market-aligned job requisitions with intelligent templates and real-time salary data.
              </p>
            </div>
          </div>

          <div className="relative z-10 grid grid-cols-3 gap-6 mt-16">
            <div className="space-y-2">
              <Sparkles className="w-8 h-8 text-blue-200" />
              <p className="text-sm font-medium">AI Generation</p>
              <p className="text-xs text-blue-200">Instant job descriptions</p>
            </div>
            <div className="space-y-2">
              <TrendingUp className="w-8 h-8 text-blue-200" />
              <p className="text-sm font-medium">Market Data</p>
              <p className="text-xs text-blue-200">Real-time insights</p>
            </div>
            <div className="space-y-2">
              <Users className="w-8 h-8 text-blue-200" />
              <p className="text-sm font-medium">Personal work space</p>
              <p className="text-xs text-blue-200">Your private workspace</p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8 text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Briefcase className="w-8 h-8 text-blue-900" />
                <div>
                  <h1 className="text-2xl font-bold text-blue-900">SmartReqs</h1>
                  <p className="text-xs text-slate-600">CaioAbruzzo LLC</p>
                </div>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Welcome Back</CardTitle>
                <CardDescription>Sign in to your account to continue</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="text"
                      placeholder="you@company.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
                
                <div className="mt-6 pt-6 border-t border-slate-200 text-center">
                  <p className="text-sm text-slate-600 mb-3">Don&apos;t have an account?</p>
                  <Button
                    variant="outline"
                    className="w-full mb-3"
                    onClick={() => navigate("/pricing")}
                    data-testid="create-account-btn"
                  >
                    Create Account
                  </Button>
                  
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">Or</span>
                    </div>
                  </div>
                  
                  <Button
                    variant="default"
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    onClick={() => navigate("/try")}
                    data-testid="try-smartreqs-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Try SmartReqs Free
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">No account required • See AI in action</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Capabilities Showcase Section */}
      <div className="w-full bg-slate-50 py-16 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">See SmartReqs in Action</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From healthcare to tech, finance to aerospace — generate professional job requisitions tailored to any industry in minutes.
            </p>
          </div>

          {/* Example Requisitions Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {/* Example 1: Healthcare */}
            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4">
                <div className="flex items-center gap-2 text-white">
                  <Building2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Healthcare</span>
                </div>
                <h3 className="text-white font-bold mt-2">Senior Registered Nurse</h3>
                <p className="text-emerald-100 text-sm">Bayside Medical Center</p>
              </div>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>Boston, MA</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-emerald-600">$130,857 - $200,706/year</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <GraduationCap className="w-4 h-4 text-slate-400" />
                  <span>BSN Required</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>5-7 years experience</span>
                </div>
              </CardContent>
            </Card>

            {/* Example 2: Technology */}
            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4">
                <div className="flex items-center gap-2 text-white">
                  <Building2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Technology</span>
                </div>
                <h3 className="text-white font-bold mt-2">Senior Data Scientist</h3>
                <p className="text-blue-100 text-sm">DataMind AI</p>
              </div>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>San Francisco, CA</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-blue-600">$203,259 - $312,150/year</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <GraduationCap className="w-4 h-4 text-slate-400" />
                  <span>PhD/Master&apos;s Preferred</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>5-8 years experience</span>
                </div>
              </CardContent>
            </Card>

            {/* Example 3: Finance */}
            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-4">
                <div className="flex items-center gap-2 text-white">
                  <Building2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Finance</span>
                </div>
                <h3 className="text-white font-bold mt-2">Investment Banking Analyst</h3>
                <p className="text-amber-100 text-sm">Sterling Capital Partners</p>
              </div>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>New York, NY</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-amber-600">$122,964 - $183,147/year</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <GraduationCap className="w-4 h-4 text-slate-400" />
                  <span>MBA or Finance Degree</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>2-4 years experience</span>
                </div>
              </CardContent>
            </Card>

            {/* Example 4: Aerospace */}
            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-r from-slate-600 to-slate-800 p-4">
                <div className="flex items-center gap-2 text-white">
                  <Building2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Aerospace & Defense</span>
                </div>
                <h3 className="text-white font-bold mt-2">Aerospace Systems Engineer</h3>
                <p className="text-slate-300 text-sm">Horizon Aerospace Industries</p>
              </div>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>Huntsville, AL</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-slate-700">$84,382 - $103,397/year</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <GraduationCap className="w-4 h-4 text-slate-400" />
                  <span>Master&apos;s in Aerospace Eng.</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>7-10 years + Secret Clearance</span>
                </div>
              </CardContent>
            </Card>

            {/* Example 5: Retail/Supply Chain */}
            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-r from-purple-500 to-violet-600 p-4">
                <div className="flex items-center gap-2 text-white">
                  <Building2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Retail & Logistics</span>
                </div>
                <h3 className="text-white font-bold mt-2">Supply Chain Director</h3>
                <p className="text-purple-100 text-sm">Atlas Retail Corporation</p>
              </div>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>Chicago, IL</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-purple-600">$135,540 - $175,690/year</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <GraduationCap className="w-4 h-4 text-slate-400" />
                  <span>MBA with Supply Chain</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>12-15 years experience</span>
                </div>
              </CardContent>
            </Card>

            {/* Example 6: Energy */}
            <Card className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-4">
                <div className="flex items-center gap-2 text-white">
                  <Building2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Energy</span>
                </div>
                <h3 className="text-white font-bold mt-2">Environmental Compliance Mgr</h3>
                <p className="text-green-100 text-sm">Gulf Energy Solutions</p>
              </div>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>Houston, TX</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-green-600">$72,425 - $93,241/year</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <GraduationCap className="w-4 h-4 text-slate-400" />
                  <span>BS Environmental Science</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>6-9 years experience</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Capabilities Section */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-slate-900">Powerful Capabilities</h3>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">AI-Powered Generation</h4>
                  <p className="text-sm text-slate-600">Enter a job title and location — our AI creates complete job descriptions, requirements, and qualifications tailored to your industry.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Real-Time Salary Intelligence</h4>
                  <p className="text-sm text-slate-600">Get market-competitive salary ranges backed by data from multiple sources including BLS, job boards, and industry benchmarks.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Smart Education Matching</h4>
                  <p className="text-sm text-slate-600">Specify required and preferred education levels with field-specific options for healthcare, technical, and general academic tracks.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Download className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Export Anywhere</h4>
                  <p className="text-sm text-slate-600">Download your requisitions as PDF, Word documents, or copy formatted text directly to your ATS or job posting platform.</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-slate-900">How It Works</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Enter Job Details</h4>
                    <p className="text-sm text-slate-600">Provide the job title, location, department, and any specific requirements you need.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">AI Generates Your Requisition</h4>
                    <p className="text-sm text-slate-600">Our AI analyzes market data and creates a complete, professional job requisition in seconds.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Review & Customize</h4>
                    <p className="text-sm text-slate-600">Edit any section, adjust requirements, and refine the content to match your exact needs.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Save & Export</h4>
                    <p className="text-sm text-slate-600">Save to your library for reuse, or export directly to PDF, Word, or your clipboard.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Workspace Preview Section */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Your Complete Hiring Workspace</h3>
              <p className="text-slate-600 max-w-2xl mx-auto">
                See how SmartReqs organizes your job requisitions from creation to publishing.
              </p>
            </div>

            {/* Workspace Previews */}
            <div className="space-y-12">
              {/* Dashboard Preview */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-3">
                    Dashboard
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-3">All Your Requisitions at a Glance</h4>
                  <p className="text-slate-600 mb-4">
                    Your dashboard shows all active requisitions with key details — salary ranges, locations, and status. 
                    Quickly access any requisition to edit, export, or share.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-emerald-600 text-xs">✓</span>
                      </div>
                      Track requisition count and status
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-emerald-600 text-xs">✓</span>
                      </div>
                      One-click access to edit or export
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-emerald-600 text-xs">✓</span>
                      </div>
                      Visual salary and location summary
                    </li>
                  </ul>
                </div>
                <div className="order-1 md:order-2">
                  {/* Dashboard Mock */}
                  <div className="bg-white rounded-xl shadow-xl border overflow-hidden">
                    <div className="bg-slate-800 px-4 py-2 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      </div>
                      <span className="text-slate-400 text-xs ml-2">SmartReqs Dashboard</span>
                    </div>
                    <div className="p-4 bg-slate-50">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <div className="text-lg font-bold text-slate-800">My Requisitions</div>
                          <div className="text-sm text-slate-500">14 Total Requisitions</div>
                        </div>
                        <div className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm">+ New Requisition</div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white p-3 rounded-lg border flex justify-between items-center">
                          <div>
                            <div className="font-medium text-slate-800">Senior Data Scientist</div>
                            <div className="text-xs text-slate-500">San Francisco, CA • $185K-$280K</div>
                          </div>
                          <div className="text-xs text-blue-600">Edit →</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border flex justify-between items-center">
                          <div>
                            <div className="font-medium text-slate-800">Investment Banking Analyst</div>
                            <div className="text-xs text-slate-500">New York, NY • $176K-$223K</div>
                          </div>
                          <div className="text-xs text-blue-600">Edit →</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border flex justify-between items-center">
                          <div>
                            <div className="font-medium text-slate-800">Senior Registered Nurse</div>
                            <div className="text-xs text-slate-500">Boston, MA • $99K-$153K</div>
                          </div>
                          <div className="text-xs text-blue-600">Edit →</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Requisition Detail Preview */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  {/* Requisition Detail Mock */}
                  <div className="bg-white rounded-xl shadow-xl border overflow-hidden">
                    <div className="bg-slate-800 px-4 py-2 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      </div>
                      <span className="text-slate-400 text-xs ml-2">Requisition Detail</span>
                    </div>
                    <div className="p-4 bg-slate-50">
                      <div className="bg-white rounded-lg border p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h5 className="font-bold text-slate-800">Senior Data Scientist</h5>
                            <p className="text-sm text-slate-500">DataMind AI • San Francisco, CA</p>
                          </div>
                          <div className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs font-medium">
                            $185K - $280K
                          </div>
                        </div>
                        <div className="text-xs text-slate-600 mb-3 line-clamp-3">
                          We are seeking a Senior Data Scientist to lead ML initiatives and build recommendation systems serving 50M+ users. You will work with cutting-edge technologies...
                        </div>
                        <div className="flex gap-2 mb-3">
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">Python</span>
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">TensorFlow</span>
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">ML</span>
                        </div>
                        <div className="flex gap-2">
                          <div className="bg-slate-100 px-2 py-1 rounded text-xs text-slate-600">📄 PDF</div>
                          <div className="bg-slate-100 px-2 py-1 rounded text-xs text-slate-600">📝 Word</div>
                          <div className="bg-slate-100 px-2 py-1 rounded text-xs text-slate-600">📋 Copy</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-3">
                    Generated Output
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-3">Complete, Professional Requisitions</h4>
                  <p className="text-slate-600 mb-4">
                    Each generated requisition includes a full job description, requirements, skills, education criteria, 
                    and market-competitive salary ranges — all formatted and ready to use.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-emerald-600 text-xs">✓</span>
                      </div>
                      AI-written job descriptions
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-emerald-600 text-xs">✓</span>
                      </div>
                      Skills and qualifications auto-populated
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-emerald-600 text-xs">✓</span>
                      </div>
                      Export to PDF, Word, or clipboard
                    </li>
                  </ul>
                </div>
              </div>

              {/* Library Preview */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-3">
                    Library
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-3">Save & Reuse Your Best Templates</h4>
                  <p className="text-slate-600 mb-4">
                    Build a library of your most-used requisitions. Quickly duplicate and customize templates 
                    for similar roles, saving hours on recurring hiring needs.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 text-xs">✓</span>
                      </div>
                      Save requisitions for future use
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 text-xs">✓</span>
                      </div>
                      One-click duplication
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 text-xs">✓</span>
                      </div>
                      Organize by department or role type
                    </li>
                  </ul>
                </div>
                <div className="order-1 md:order-2">
                  {/* Library Mock */}
                  <div className="bg-white rounded-xl shadow-xl border overflow-hidden">
                    <div className="bg-slate-800 px-4 py-2 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      </div>
                      <span className="text-slate-400 text-xs ml-2">Requisition Library</span>
                    </div>
                    <div className="p-4 bg-slate-50">
                      <div className="mb-4">
                        <div className="text-lg font-bold text-slate-800">My Library</div>
                        <div className="text-sm text-slate-500">14 Saved Templates</div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white p-3 rounded-lg border">
                          <div className="font-medium text-sm text-slate-800 truncate">DevOps Engineer</div>
                          <div className="text-xs text-slate-500">Seattle, WA</div>
                          <div className="text-xs text-emerald-600 mt-1">$176K-$247K</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border">
                          <div className="font-medium text-sm text-slate-800 truncate">UX Design Lead</div>
                          <div className="text-xs text-slate-500">Austin, TX</div>
                          <div className="text-xs text-emerald-600 mt-1">$173K-$212K</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border">
                          <div className="font-medium text-sm text-slate-800 truncate">Supply Chain Dir.</div>
                          <div className="text-xs text-slate-500">Chicago, IL</div>
                          <div className="text-xs text-emerald-600 mt-1">$204K-$276K</div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border">
                          <div className="font-medium text-sm text-slate-800 truncate">Research Scientist</div>
                          <div className="text-xs text-slate-500">San Diego, CA</div>
                          <div className="text-xs text-emerald-600 mt-1">$170K-$182K</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to streamline your hiring process?</h3>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              Join HR professionals who save hours creating job requisitions. Try SmartReqs free — no account required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50"
                onClick={() => navigate("/try")}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Try It Free Now
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                onClick={() => navigate("/pricing")}
              >
                View Pricing
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full bg-slate-900 text-white py-8 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <Briefcase className="w-6 h-6" />
              <div>
                <span className="font-bold">SmartReqs</span>
                <span className="text-slate-400 text-sm ml-2">by CaioAbruzzo LLC</span>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                onClick={() => navigate("/guide")}
                data-testid="landing-guide-btn"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                User Guide
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                onClick={() => navigate("/faq")}
                data-testid="landing-faq-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                FAQ
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                onClick={() => navigate("/sitemap")}
                data-testid="landing-sitemap-btn"
              >
                <Map className="w-4 h-4 mr-2" />
                Sitemap
              </Button>
              <a 
                href="mailto:admin@smartreqs.ai" 
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>admin@smartreqs.ai</span>
              </a>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-slate-800 text-center text-slate-500 text-sm">
            © {new Date().getFullYear()} CaioAbruzzo LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
